import { useState, useEffect } from "react";
import { useApi, apiPut, apiPost, apiDelete } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, ShieldCheck, Bot, Wallet, Save, UserPlus, Trash2, Users } from "lucide-react";

const REGIONS = ["UAE (Dubai)", "India (Mumbai)", "EU (Dublin)", "US (Virginia)", "UK (London)"];
const RESIDENCY = ["EU", "US", "India", "UAE", "Global"];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED"];
const POLICIES = ["Allow", "Coach", "Redact", "Block"];
const FORMATS = ["PDF", "PPTX", "CSV"];
const MONTHS = ["January", "April", "July", "October"];

export default function TenantSettings() {
  const { user } = useApp();
  const { data, isLoading, error } = useApi("tenant-settings", "/tenant/settings");
  const { data: profiles = [], refetch: refetchUsers } = useApi("all-profiles", "/auth/profiles");
  const { data: roles = [] } = useApi("roles", "/roles");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nu, setNu] = useState({ name: "", email: "", role_key: "employee" });
  useEffect(() => { if (data && !form) setForm(data); }, [data]); // eslint-disable-line

  const onboard = async () => {
    if (!nu.name.trim() || !nu.email.trim()) return toast.error("Name and company email are required");
    try { await apiPost("/users", { ...nu, actor_role_key: user?.role_key }); toast.success(`${nu.name} onboarded`); setNu({ name: "", email: "", role_key: "employee" }); refetchUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Onboard failed"); }
  };
  const offboard = async (p) => {
    try { await apiDelete(`/users/${p.id}?actor_role_key=${user?.role_key}&actor_id=${user?.id}`); toast.success(`${p.name} offboarded`); refetchUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Offboard failed"); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => { setSaving(true); try { await apiPut("/tenant/settings", form); toast.success("Tenant settings saved"); } catch { toast.error("Save failed"); } setSaving(false); };

  if (error) return <ErrorState />;
  if (isLoading || !form) return <LoadingBlock />;

  const Field = ({ label, k, type = "text" }) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`ts-${k}`} /></div>
  );
  const Sel = ({ label, k, opts }) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Select value={String(form[k])} onValueChange={(v) => set(k, v)}><SelectTrigger data-testid={`ts-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
  );
  const Toggle = ({ label, k, desc }) => (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div><p className="text-sm font-medium">{label}</p>{desc && <p className="text-[11px] text-muted-foreground">{desc}</p>}</div>
      <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`ts-${k}`} /></div>
  );

  return (
    <div className="space-y-4" data-testid="tenant-settings">
      <PageHeader eyebrow="Navitra Core" title="Tenant Settings" subtitle="Organisation-wide configuration for your Navitra OS tenant.">
        <Button size="sm" onClick={save} disabled={saving} data-testid="save-settings"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-navy" />Organisation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field label="Organisation name" k="org_name" />
            <div className="grid grid-cols-2 gap-3"><Sel label="Primary region" k="primary_region" opts={REGIONS} /><Sel label="Data residency" k="data_residency" opts={RESIDENCY} /></div>
            <div className="grid grid-cols-2 gap-3"><Sel label="Default currency" k="default_currency" opts={CURRENCIES} /><Sel label="Fiscal year start" k="fiscal_year_start" opts={MONTHS} /></div>
            <Field label="Support contact" k="support_contact" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-navy" />Security &amp; access</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Toggle label="Enforce SSO" k="enforce_sso" desc="Require identity-provider sign-in for all users." />
            <Toggle label="Require MFA" k="require_mfa" desc="Multi-factor authentication for admin roles." />
            <Field label="Session timeout (minutes)" k="session_timeout_mins" type="number" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Bot className="h-4 w-4 text-navy" />AI defaults</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Sel label="Default AI policy action" k="default_ai_policy" opts={POLICIES} />
            <Toggle label="Allow shadow AI (unsanctioned tools)" k="allow_shadow_ai" desc="When off, unsanctioned tools are blocked by default." />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4 text-navy" />Budget &amp; compliance</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3"><Field label="Monthly budget (USD)" k="monthly_budget_usd" type="number" /><Field label="Budget alert at (%)" k="budget_alert_pct" type="number" /></div>
            <div className="grid grid-cols-2 gap-3"><Field label="Data retention (days)" k="retention_days" type="number" /><Sel label="Audit export format" k="audit_export_format" opts={FORMATS} /></div>
          </CardContent>
        </Card>
      </div>

      {/* User management — tenant admin only */}
      <Card data-testid="user-management">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-navy" />Users ({profiles.length})<Badge variant="outline" className="ml-1 text-[10px]">Tenant admin only</Badge></CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_1fr_160px_auto] gap-2 items-end rounded-lg border bg-muted/30 p-3">
            <div className="space-y-1"><Label className="text-xs">Full name</Label><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Jordan Miles" data-testid="onboard-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Company email</Label><Input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="jordan@profecia.example" data-testid="onboard-email" /></div>
            <div className="space-y-1"><Label className="text-xs">Role</Label>
              <Select value={nu.role_key} onValueChange={(v) => setNu({ ...nu, role_key: v })}><SelectTrigger data-testid="onboard-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.filter((r) => !r.locked).map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={onboard} data-testid="onboard-user"><UserPlus className="h-4 w-4 mr-1.5" />Onboard</Button>
          </div>
          <div className="rounded-lg border divide-y max-h-72 overflow-y-auto">
            {profiles.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2" data-testid={`user-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2">{p.name}{p.custom && <Badge className="bg-gold text-nav-text text-[9px] px-1 py-0">onboarded</Badge>}{p.id === user?.id && <Badge className="text-[9px] px-1 py-0">You</Badge>}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{p.email} · {p.role}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-sev-danger hover:text-sev-danger disabled:opacity-30" disabled={!p.custom || p.id === user?.id} onClick={() => offboard(p)} data-testid={`offboard-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Built-in profiles and your own account cannot be offboarded. Only onboarded users can be removed.</p>
        </CardContent>
      </Card>
    </div>
  );
}
